import React from 'react';

interface CustomColors {
  whiteMigo: string;
  blackMigo: string;
  yugoColor: string;
  whiteYugo: string;
  blackYugo: string;
  boardColor: string;
  hoverColor: string;
  lastMoveColor: string;
}

interface SettingsModalProps {
  isOpen: boolean;
  currentTheme: string;
  customColors: CustomColors;
  gameSoundsEnabled: boolean;
  notificationsEnabled: boolean;
  illegalHoverEnabled: boolean;
  dmPrivacy?: 'everyone' | 'nobody';
  isAuthenticated?: boolean;
  onClose: () => void;
  onThemeChange: (theme: string) => void;
  onCustomColorChange: (colorType: keyof CustomColors, color: string) => void | Promise<void>;
  onGameSoundsToggle: (enabled: boolean) => void;
  onNotificationsToggle: (enabled: boolean) => void;
  onIllegalHoverToggle: (enabled: boolean) => void;
  onDmPrivacyChange?: (value: 'everyone' | 'nobody') => void;
  onResetSettings: () => void;
}

export const SettingsModal: React.FC<SettingsModalProps> = ({
  isOpen,
  currentTheme,
  customColors,
  gameSoundsEnabled,
  notificationsEnabled,
  illegalHoverEnabled,
  dmPrivacy = 'everyone',
  isAuthenticated = false,
  onClose,
  onThemeChange,
  onCustomColorChange,
  onGameSoundsToggle,
  onNotificationsToggle,
  onIllegalHoverToggle,
  onDmPrivacyChange,
  onResetSettings
}) => {
  if (!isOpen) {
    return null;
  }

  return (
    <>
      <div className="overlay" style={{ display: 'block' }} onClick={onClose} />
      <div className="notification settings-dialog" style={{ display: 'block' }}>
        <h2>Settings</h2>
        
        {/* Theme Section */}
        <div className="settings-section">
          <h3>Theme</h3>
          <div className="option-row">
            <label>Theme:</label>
            <select 
              className="control-select" 
              value={currentTheme} 
              onChange={(e) => onThemeChange(e.target.value)}
            >
              <option value="classic">Classic</option>
              <option value="high-contrast">High Contrast</option>
              <option value="galaxy">Galaxy/Space</option>
              <option value="neon">Neon/Cyberpunk</option>
              <option value="marble">Marble</option>
              <option value="wood">Wood</option>
              <option value="custom">Custom</option>
            </select>
          </div>
          <div className="option-row">
            <label>Illegal hover:</label>
            <div className="toggle-container">
              <span className={`toggle-label ${!illegalHoverEnabled ? 'active' : ''}`}>Off</span>
              <label className="toggle">
                <input
                  type="checkbox"
                  checked={illegalHoverEnabled}
                  onChange={(e) => onIllegalHoverToggle(e.target.checked)}
                />
                <span className="slider round"></span>
              </label>
              <span className={`toggle-label ${illegalHoverEnabled ? 'active' : ''}`}>On</span>
            </div>
          </div>
            
          {/* Custom Colors (only show when custom theme selected) */}
          {currentTheme === 'custom' && (
            <div id="custom-colors" style={{ marginTop: '15px', padding: '15px', backgroundColor: '#f8f9fa', borderRadius: '5px' }}>
              <h4 style={{ marginBottom: '10px', fontSize: '1rem' }}>Custom Colors</h4>
              
              <div className="color-option">
                <label>White Migo Color:</label>
                <input
                  type="color"
                  value={customColors.whiteMigo}
                  onChange={(e) => onCustomColorChange('whiteMigo', e.target.value)}
                />
              </div>
              
              <div className="color-option">
                <label>Black Migo Color:</label>
                <input
                  type="color"
                  value={customColors.blackMigo}
                  onChange={(e) => onCustomColorChange('blackMigo', e.target.value)}
                />
              </div>
              
              <div className="color-option">
                <label>White Yugo:</label>
                <input
                  type="color"
                  value={customColors.whiteYugo}
                  onChange={(e) => onCustomColorChange('whiteYugo', e.target.value)}
                />
              </div>
              
              <div className="color-option">
                <label>Black Yugo:</label>
                <input
                  type="color"
                  value={customColors.blackYugo}
                  onChange={(e) => onCustomColorChange('blackYugo', e.target.value)}
                />
              </div>
              
              <div className="color-option">
                <label>Board Color:</label>
                <input
                  type="color"
                  value={customColors.boardColor}
                  onChange={(e) => onCustomColorChange('boardColor', e.target.value)}
                />
              </div>
              
              <div className="color-option">
                <label>Hover Color:</label>
                <input
                  type="color"
                  value={customColors.hoverColor}
                  onChange={(e) => onCustomColorChange('hoverColor', e.target.value)}
                />
              </div>
              
              <div className="color-option">
                <label>Last Move Indicator:</label>
                <input
                  type="color"
                  value={customColors.lastMoveColor}
                  onChange={(e) => onCustomColorChange('lastMoveColor', e.target.value)}
                />
              </div>
            </div>
          )}
        </div>

        {/* Sound Section */}
        <div className="settings-section">
          <h3>Sound</h3>
          <div className="option-row">
            <label>Game Sounds:</label>
            <div className="toggle-container">
              <span className={`toggle-label ${!gameSoundsEnabled ? 'active' : ''}`}>Off</span>
              <label className="toggle">
                <input
                  type="checkbox"
                  checked={gameSoundsEnabled}
                  onChange={(e) => onGameSoundsToggle(e.target.checked)}
                />
                <span className="slider round"></span>
              </label>
              <span className={`toggle-label ${gameSoundsEnabled ? 'active' : ''}`}>On</span>
            </div>
          </div>
          <div className="option-row">
            <label>Notifications:</label>
            <div className="toggle-container">
              <span className={`toggle-label ${!notificationsEnabled ? 'active' : ''}`}>Off</span>
              <label className="toggle">
                <input
                  type="checkbox"
                  checked={notificationsEnabled}
                  onChange={(e) => onNotificationsToggle(e.target.checked)}
                />
                <span className="slider round"></span>
              </label>
              <span className={`toggle-label ${notificationsEnabled ? 'active' : ''}`}>On</span>
            </div>
          </div>
        </div>

        {/* Privacy Section - only for authenticated users */}
        {isAuthenticated && onDmPrivacyChange && (
          <div className="settings-section">
            <h3>Privacy</h3>
            <div className="option-row">
              <label>Who can message me:</label>
              <select
                className="control-select"
                value={dmPrivacy}
                onChange={(e) => onDmPrivacyChange(e.target.value as 'everyone' | 'nobody')}
              >
                <option value="everyone">Everyone</option>
                <option value="nobody">Nobody</option>
              </select>
            </div>
            <p style={{ fontSize: '0.85rem', color: '#888', margin: '8px 0 0 0' }}>
              {dmPrivacy === 'nobody'
                ? 'Other players will see that you have messaging disabled.'
                : 'Any player can send you a direct message.'}
            </p>
          </div>
        )}

        {/* Buttons */}
        <div className="notification-buttons">
          <button className="btn" onClick={onResetSettings}>Reset to Classic</button>
          <button className="btn" onClick={onClose}>Close</button>
        </div>
      </div>
    </>
  );
};

